import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Terminal, CircleCheck, Loader2, Code2, Sparkles } from "lucide-react";
import { useContent } from "@/lib/cms";

/* ──────────────────────────────────────────────────────────────────
   Syntax-highlighted code lines (tiny custom highlighter — no deps)
   ────────────────────────────────────────────────────────────────── */

const METHOD_COLORS = {
  GET:    "text-cyan-300",
  POST:   "text-emerald-300",
  PUT:    "text-amber-300",
  PATCH:  "text-violet-300",
  DELETE: "text-rose-300",
};

const METHOD_BADGE = {
  GET:    "bg-cyan-500/15 text-cyan-300 ring-cyan-400/30",
  POST:   "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30",
  PUT:    "bg-amber-500/15 text-amber-300 ring-amber-400/30",
  PATCH:  "bg-violet-500/15 text-violet-300 ring-violet-400/30",
  DELETE: "bg-rose-500/15 text-rose-300 ring-rose-400/30",
};

function tokenizeLine(line, kind) {
  if (kind === "request") {
    const m = line.match(/^(\S+)\s+(.+)$/);
    if (!m) return [{ text: line, cls: "text-slate-200" }];
    const method = m[1].toUpperCase();
    return [
      { text: m[1], cls: `font-bold ${METHOD_COLORS[method] || "text-emerald-300"}` },
      { text: " ", cls: "" },
      { text: m[2], cls: "text-blue-200" },
    ];
  }
  if (kind === "header") {
    const idx = line.indexOf(":");
    if (idx < 0) return [{ text: line, cls: "text-slate-200" }];
    return [
      { text: line.slice(0, idx), cls: "text-fuchsia-300" },
      { text: ":", cls: "text-slate-500" },
      { text: line.slice(idx + 1), cls: "text-slate-200" },
    ];
  }
  if (kind === "json") {
    const tokens = [];
    const re = /("(?:[^"\\]|\\.)*")|(\b\d+(?:\.\d+)?\b)|(\btrue\b|\bfalse\b|\bnull\b)|([{}\[\],:])|(\s+)|([^"\d\s{}\[\],:]+)/g;
    let m;
    let keyExpected = true;
    while ((m = re.exec(line)) !== null) {
      const [, str, num, kw, punct, ws, other] = m;
      if (str !== undefined) {
        tokens.push({ text: str, cls: keyExpected ? "text-emerald-300" : "text-amber-200" });
      } else if (num !== undefined) {
        tokens.push({ text: num, cls: "text-orange-300" });
      } else if (kw !== undefined) {
        tokens.push({ text: kw, cls: "text-violet-300" });
      } else if (punct !== undefined) {
        if (punct === ":") keyExpected = false;
        else if (punct === "," || punct === "{") keyExpected = true;
        tokens.push({ text: punct, cls: "text-slate-500" });
      } else if (ws !== undefined) {
        tokens.push({ text: ws, cls: "" });
      } else if (other !== undefined) {
        tokens.push({ text: other, cls: "text-slate-200" });
      }
    }
    return tokens;
  }
  return [{ text: line, cls: "text-slate-300" }];
}

function buildRequestLines(example) {
  const lines = [];
  lines.push({ kind: "request", text: `${(example.method || "POST").toUpperCase()} ${example.endpoint || ""}` });
  for (const h of example.headers || []) {
    if (!h?.key) continue;
    lines.push({ kind: "header", text: `${h.key}: ${h.value ?? ""}` });
  }
  const body = (example.body_json || "").trim();
  if (body) {
    lines.push({ kind: "blank", text: "" });
    body.split("\n").forEach((l) => lines.push({ kind: "json", text: l }));
  }
  return lines;
}
function totalChars(lines) {
  return lines.reduce((acc, l, i) => acc + l.text.length + (i < lines.length - 1 ? 1 : 0), 0);
}

/* ──────────────────────────────────────────────────────────────────
   Tab labels — concise developer-friendly titles per example
   ────────────────────────────────────────────────────────────────── */

const DEFAULT_TAB_BY_ID = {
  "ex-send":       "Send Message API",
  "ex-list":       "Get Messages API",
  "ex-webhook":    "Webhook Events",
  "ex-templates":  "Template API",
  "ex-media":      "Media Upload",
};
function tabLabel(ex) {
  if (ex.tab_label) return ex.tab_label;
  if (DEFAULT_TAB_BY_ID[ex.id]) return DEFAULT_TAB_BY_ID[ex.id];
  const m = (ex.method || "POST").toUpperCase();
  const last = (ex.endpoint || "").split("/").filter(Boolean).pop() || "endpoint";
  return `${m} /${last}`;
}

/* ──────────────────────────────────────────────────────────────────
   Window chrome with breathing animation + floating shadow + glow
   ────────────────────────────────────────────────────────────────── */

const Window = ({ filename, method, children, breathing = true }) => (
  <motion.div
    animate={breathing ? { boxShadow: [
      "0 30px 80px -20px rgba(0,0,0,0.55), 0 0 0 1px rgba(56,189,248,0.06)",
      "0 36px 90px -18px rgba(0,0,0,0.65), 0 0 0 1px rgba(16,185,129,0.10)",
      "0 30px 80px -20px rgba(0,0,0,0.55), 0 0 0 1px rgba(56,189,248,0.06)",
    ] } : undefined}
    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
    className="relative rounded-2xl bg-gradient-to-b from-[#0b1220] to-[#070a15] ring-1 ring-white/10"
  >
    {/* outer glow */}
    <div className="pointer-events-none absolute -inset-[1px] rounded-2xl bg-[radial-gradient(40%_60%_at_50%_0%,rgba(56,189,248,0.18),transparent_70%)]" />
    {/* chrome */}
    <div className="relative flex items-center gap-2 px-4 py-3 border-b border-white/5">
      <span className="h-3 w-3 rounded-full bg-rose-400/80" />
      <span className="h-3 w-3 rounded-full bg-amber-300/80" />
      <span className="h-3 w-3 rounded-full bg-emerald-400/80" />
      <div className="mx-auto text-[11.5px] font-medium text-slate-300/80 tracking-wide flex items-center gap-1.5">
        {method && (
          <span className={`px-1.5 py-0.5 rounded-md ring-1 text-[9.5px] font-mono font-bold tracking-wider ${METHOD_BADGE[method] || "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30"}`}>
            {method}
          </span>
        )}
        <Terminal className="h-3 w-3 text-emerald-300/80" />
        <span className="font-mono">{filename}</span>
      </div>
    </div>
    {children}
  </motion.div>
);

const Cursor = () => (
  <span aria-hidden className="inline-block w-[7px] h-[14px] -mb-[2px] ml-[1px] bg-emerald-300/90 align-middle animate-pulse" />
);

const StatusPill = ({ label, state, glow = false }) => {
  const base = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold font-mono whitespace-nowrap";
  if (state === "done") {
    return (
      <motion.span
        initial={{ scale: 0.92, opacity: 0 }}
        animate={glow ? { scale: [1, 1.06, 1], opacity: 1 } : { scale: 1, opacity: 1 }}
        transition={glow ? { scale: { duration: 1.6, repeat: Infinity, ease: "easeInOut" }, opacity: { duration: 0.25 } } : { duration: 0.25 }}
        className={`${base} bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30 shadow-[0_0_24px_-4px_rgba(16,185,129,0.7)]`}
      >
        <CircleCheck className="h-3 w-3" /> {label}
      </motion.span>
    );
  }
  if (state === "active") {
    return (
      <motion.span
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
        className={`${base} bg-blue-500/15 text-blue-200 ring-1 ring-blue-400/30`}
      >
        <Loader2 className="h-3 w-3 animate-spin" /> {label}
      </motion.span>
    );
  }
  return (
    <span className={`${base} bg-white/5 text-slate-500 ring-1 ring-white/5`}>
      <span className="h-1.5 w-1.5 rounded-full bg-slate-600" /> {label}
    </span>
  );
};

/* ──────────────────────────────────────────────────────────────────
   Code Editor
   Phases:
     "typing"     — request types char-by-char
     "sending"    — pre_statuses banner (Request sent → Processing → …)
     "ok"         — animated 200 OK badge appears
     "responding" — response lines stream in line-by-line
     "status"     — status_flow pills (Queued → Sending → Delivered ✓)
     "hold"       — final glow, then onDone()
   ────────────────────────────────────────────────────────────────── */

const CodeEditor = ({ example, settings, onDone }) => {
  const requestLines = useMemo(() => buildRequestLines(example), [example]);
  const responseLines = useMemo(
    () => (example.response_json || "").split("\n").map((t) => ({ kind: "json", text: t })),
    [example]
  );
  const total = useMemo(() => totalChars(requestLines), [requestLines]);

  // CMS-driven pre-response banner statuses
  const preStatuses = useMemo(() => {
    const s = settings?.pre_statuses;
    if (Array.isArray(s) && s.length > 0) return s;
    return ["Request sent...", "Processing..."];
  }, [settings?.pre_statuses]);

  const [phase, setPhase] = useState("typing");
  const [typed, setTyped] = useState(0);
  const [respShown, setRespShown] = useState(0);
  const [preStatusIdx, setPreStatusIdx] = useState(-1);
  const [statusIdx, setStatusIdx] = useState(-1);
  const timers = useRef([]);

  // Auto-scroll the request panel as typing progresses
  const reqScrollRef = useRef(null);

  useEffect(() => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
    setPhase("typing");
    setTyped(0);
    setRespShown(0);
    setPreStatusIdx(-1);
    setStatusIdx(-1);
  }, [example.id]);

  // Typing
  useEffect(() => {
    if (phase !== "typing") return;
    if (typed >= total) {
      const t = setTimeout(() => setPhase("sending"), settings.pause_after_typing_ms ?? 600);
      timers.current.push(t);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setTyped((c) => c + 1), settings.typing_speed_ms ?? 22);
    timers.current.push(t);
    return () => clearTimeout(t);
  }, [phase, typed, total, settings.pause_after_typing_ms, settings.typing_speed_ms]);

  // auto-scroll while typing
  useEffect(() => {
    const el = reqScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [typed]);

  // "Sending" pre-status flow (CMS-driven via settings.pre_statuses)
  useEffect(() => {
    if (phase !== "sending") return;
    if (preStatusIdx === -1) { setPreStatusIdx(0); return; }
    if (preStatusIdx >= preStatuses.length - 1) {
      const t = setTimeout(() => setPhase("ok"), settings.status_step_ms ?? 700);
      timers.current.push(t);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setPreStatusIdx((i) => i + 1), settings.status_step_ms ?? 700);
    timers.current.push(t);
    return () => clearTimeout(t);
  }, [phase, preStatusIdx, settings.status_step_ms, preStatuses.length]);

  // 200 OK badge → response stream
  useEffect(() => {
    if (phase !== "ok") return;
    const t = setTimeout(() => setPhase("responding"), 550);
    timers.current.push(t);
    return () => clearTimeout(t);
  }, [phase]);

  // Responding
  useEffect(() => {
    if (phase !== "responding") return;
    if (respShown >= responseLines.length) {
      const t = setTimeout(() => setPhase("status"), 350);
      timers.current.push(t);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setRespShown((n) => n + 1), 110);
    timers.current.push(t);
    return () => clearTimeout(t);
  }, [phase, respShown, responseLines.length]);

  // Status flow
  const statuses = example.status_flow && example.status_flow.length > 0
    ? example.status_flow
    : ["Queued...", "Sending...", "Delivered ✓"];

  useEffect(() => {
    if (phase !== "status") return;
    if (statusIdx === -1) { setStatusIdx(0); return; }
    if (statusIdx >= statuses.length - 1) {
      const t = setTimeout(() => setPhase("hold"), settings.status_step_ms ?? 800);
      timers.current.push(t);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setStatusIdx((i) => i + 1), settings.status_step_ms ?? 800);
    timers.current.push(t);
    return () => clearTimeout(t);
  }, [phase, statusIdx, statuses.length, settings.status_step_ms]);

  // Hold then advance
  useEffect(() => {
    if (phase !== "hold") return;
    const t = setTimeout(() => onDone?.(), settings.loop_pause_ms ?? 1300);
    timers.current.push(t);
    return () => clearTimeout(t);
  }, [phase, onDone, settings.loop_pause_ms]);

  useEffect(() => () => timers.current.forEach((t) => clearTimeout(t)), []);

  // Compute which request line is "active" (currently being typed)
  let remaining = typed;
  const renderedRequest = requestLines.map((line, idx) => {
    let visibleText = "";
    if (remaining > 0) {
      visibleText = line.text.slice(0, remaining);
      remaining -= line.text.length;
      if (remaining > 0 && idx < requestLines.length - 1) remaining -= 1;
    }
    const tokens = tokenizeLine(visibleText, line.kind);
    const isActiveLine = visibleText.length > 0 && visibleText.length < line.text.length && phase === "typing";
    const isFullyTyped = visibleText.length === line.text.length;
    return (
      <div
        key={idx}
        className={`grid grid-cols-[2.25rem_1fr] font-mono text-[12.5px] leading-[1.75] rounded-md ${isActiveLine ? "bg-emerald-400/[0.06] ring-1 ring-emerald-400/15" : ""}`}
      >
        <span className={`text-right pr-3 select-none tabular-nums ${isFullyTyped || isActiveLine ? "text-slate-500" : "text-slate-700"}`}>{idx + 1}</span>
        <span className="whitespace-pre">
          {tokens.map((t, i) => (
            <span key={i} className={t.cls}>{t.text}</span>
          ))}
          {isActiveLine && <Cursor />}
        </span>
      </div>
    );
  });

  const showOk = phase === "ok" || phase === "responding" || phase === "status" || phase === "hold";
  const showResp = phase === "responding" || phase === "status" || phase === "hold";
  const showStatus = phase === "status" || phase === "hold";

  const method = (example.method || "POST").toUpperCase();
  const filename = `${method.toLowerCase()}-${(example.endpoint || "request").split("/").pop() || "request"}.http`;

  return (
    <Window filename={filename} method={method}>
      <div className="relative p-4 sm:p-5">
        {/* Request block */}
        <motion.div
          animate={phase === "typing" ? { boxShadow: "0 0 0 1px rgba(16,185,129,0.18), 0 0 40px -10px rgba(16,185,129,0.3)" } : { boxShadow: "0 0 0 1px rgba(255,255,255,0.04)" }}
          transition={{ duration: 0.5 }}
          className="rounded-xl bg-black/30 p-3 sm:p-4"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2 flex items-center gap-1.5">
            <Code2 className="h-3 w-3" /> Request
          </div>
          <div ref={reqScrollRef} className="max-h-[220px] overflow-x-auto overflow-y-hidden" data-testid="developer-api-request">
            {renderedRequest}
          </div>
        </motion.div>

        {/* Pre-response inline status banner */}
        <AnimatePresence>
          {phase === "sending" && (
            <motion.div
              key="sending"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="mt-3 rounded-xl bg-blue-500/[0.06] ring-1 ring-blue-400/20 p-3 flex items-center gap-2 font-mono text-[12px] text-blue-200"
              data-testid="developer-api-presend"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>{preStatuses[Math.max(preStatusIdx, 0)] || ""}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Response card */}
        <AnimatePresence>
          {showOk && (
            <motion.div
              key={`resp-${example.id}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.35 }}
              className="mt-3 rounded-xl bg-emerald-500/[0.04] ring-1 ring-emerald-400/20 p-3 sm:p-4"
              data-testid="developer-api-response"
            >
              <div className="flex items-center gap-2 mb-2">
                <motion.span
                  initial={{ scale: 0.85, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 18 }}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30 text-[10.5px] font-bold font-mono tracking-wider"
                  data-testid="developer-api-200"
                >
                  <Sparkles className="h-3 w-3" /> 200 OK
                </motion.span>
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-300/80">Response</span>
              </div>

              <div className="min-h-[40px]">
                {responseLines.slice(0, respShown).map((line, idx) => {
                  const tokens = tokenizeLine(line.text, line.kind);
                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.18 }}
                      className="grid grid-cols-[2.25rem_1fr] font-mono text-[12.5px] leading-[1.75]"
                    >
                      <span className="text-slate-700 text-right pr-3 select-none tabular-nums">{idx + 1}</span>
                      <span className="whitespace-pre">
                        {tokens.map((t, i) => (
                          <span key={i} className={t.cls}>{t.text}</span>
                        ))}
                      </span>
                    </motion.div>
                  );
                })}
              </div>

              {/* Status flow pills */}
              {showStatus && (
                <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="developer-api-status">
                  {statuses.map((s, i) => {
                    let state = "idle";
                    if (phase === "hold") {
                      state = "done";
                    } else if (i < statusIdx) {
                      state = "done";
                    } else if (i === statusIdx) {
                      state = i === statuses.length - 1 ? "done" : "active";
                    }
                    const isFinalDone = state === "done" && i === statuses.length - 1;
                    return (
                      <React.Fragment key={i}>
                        <StatusPill label={s} state={state} glow={isFinalDone} />
                        {i < statuses.length - 1 && <span className="text-slate-500 text-xs">→</span>}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Window>
  );
};

/* ──────────────────────────────────────────────────────────────────
   Tab bar
   ────────────────────────────────────────────────────────────────── */

const TabBar = ({ tabs, activeIdx, onPick }) => (
  <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1" data-testid="developer-api-tabs" role="tablist">
    {tabs.map((t, i) => {
      const isActive = i === activeIdx;
      return (
        <button
          key={t.id || i}
          type="button"
          role="tab"
          aria-selected={isActive}
          onClick={() => onPick(i)}
          data-testid={`developer-api-tab-${i}`}
          className={`relative inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11.5px] font-semibold whitespace-nowrap transition-colors ring-1 ${
            isActive
              ? "bg-white text-slate-900 ring-slate-900/10 shadow-[0_6px_20px_-8px_rgba(2,6,23,0.4)]"
              : "bg-white/60 hover:bg-white text-slate-600 ring-slate-200"
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-slate-300"}`} />
          {t.label}
        </button>
      );
    })}
  </div>
);

/* ──────────────────────────────────────────────────────────────────
   Section
   ────────────────────────────────────────────────────────────────── */

const DEFAULT_EXAMPLES = [
  {
    id: "ex-send",
    tab_label: "Send Message API",
    method: "POST",
    endpoint: "/v1/messages/send",
    headers: [
      { key: "Authorization", value: "Bearer YOUR_API_KEY" },
      { key: "Content-Type", value: "application/json" },
    ],
    body_json: '{\n  "to": "+919876543210",\n  "type": "text",\n  "text": {\n    "body": "Hello from Wappit API!"\n  }\n}',
    response_json: '{\n  "status": "success",\n  "message_id": "wamid.abc123xyz",\n  "timestamp": 1720000000\n}',
    status_flow: ["Queued...", "Sending...", "Delivered ✓"],
    order: 1,
    enabled: true,
  },
  {
    id: "ex-list",
    tab_label: "Get Messages API",
    method: "GET",
    endpoint: "/v1/messages?limit=10&status=delivered",
    headers: [
      { key: "Authorization", value: "Bearer YOUR_API_KEY" },
    ],
    body_json: "",
    response_json: '{\n  "messages": [\n    { "id": "wamid.001", "status": "delivered" },\n    { "id": "wamid.002", "status": "read" }\n  ],\n  "total": 2\n}',
    status_flow: ["Fetching...", "Done ✓"],
    order: 2,
    enabled: true,
  },
  {
    id: "ex-templates",
    tab_label: "Template API",
    method: "POST",
    endpoint: "/v1/templates/send",
    headers: [
      { key: "Authorization", value: "Bearer YOUR_API_KEY" },
      { key: "Content-Type", value: "application/json" },
    ],
    body_json: '{\n  "to": "+919876543210",\n  "template": "order_update",\n  "language": "en",\n  "components": [\n    { "type": "body", "parameters": [{"type": "text", "text": "#4321"}] }\n  ]\n}',
    response_json: '{\n  "status": "accepted",\n  "message_id": "wamid.tpl789"\n}',
    status_flow: ["Queued...", "Template matched", "Delivered ✓"],
    order: 3,
    enabled: true,
  },
];

export const DeveloperApi = () => {
  const { data, enabled, loaded } = useContent("developer_api");

  const examples = useMemo(() => {
    const all = (data?.examples || []).filter((e) => e?.enabled !== false);
    const sorted = all.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return sorted.length > 0 ? sorted : DEFAULT_EXAMPLES;
  }, [data]);

  const tabs = useMemo(
    () => examples.map((e) => ({ id: e.id, label: tabLabel(e) })),
    [examples]
  );

  const settings = data?.settings || {};
  const [activeIdx, setActiveIdx] = useState(0);
  const advance = () => setActiveIdx((i) => (examples.length ? (i + 1) % examples.length : 0));

  // Keep activeIdx valid if examples shrink (CMS-edit safety)
  useEffect(() => {
    if (examples.length === 0) { setActiveIdx(0); return; }
    if (activeIdx >= examples.length) setActiveIdx(0);
  }, [examples.length, activeIdx]);

  if (enabled === false) return null;

  const eyebrow = data?.eyebrow || "DEVELOPER API";
  const heading = data?.heading || "Build with Wappit APIs";
  const subtitle = data?.subtitle || "";
  const description = data?.description || "Integrate the Official WhatsApp Business API into your applications using secure REST APIs, Webhooks and powerful automation endpoints.";
  const bullets = (data?.bullets || []).slice(0, 6);
  const cta = data?.cta || { label: "Explore API Documentation", href: "#" };

  return (
    <section id="developer-api" className="relative overflow-hidden py-20 sm:py-28 bg-slate-50" data-testid="developer-api-section">
      {/* subtle grid + ambient gradient (kept identical to previous version) */}
      <div className="absolute inset-0 dot-grid opacity-50" />
      <div className="pointer-events-none absolute -top-32 left-0 h-[420px] w-[520px] rounded-full bg-emerald-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-20 h-[420px] w-[520px] rounded-full bg-blue-200/30 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[minmax(0,62fr)_minmax(0,38fr)] gap-10 lg:gap-12 xl:gap-16 items-center">
        {/* LEFT — ~62% editor */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="min-w-0 order-2 lg:order-1 overflow-x-hidden"
          data-testid="developer-api-editor-wrap"
        >
          {examples.length > 0 ? (
            <div className="space-y-3">
              <TabBar
                tabs={tabs}
                activeIdx={activeIdx % examples.length}
                onPick={(i) => setActiveIdx(i)}
              />
              {/* Editor wrapper preserves layout while swapping example to prevent any blank flash */}
              <div className="relative">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={examples[activeIdx % examples.length]?.id || activeIdx}
                    initial={{ opacity: 0, y: 6, filter: "blur(2px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, y: -6, filter: "blur(2px)" }}
                    transition={{ duration: 0.45 }}
                  >
                    <CodeEditor
                      example={examples[activeIdx % examples.length]}
                      settings={settings}
                      onDone={advance}
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          ) : loaded ? (
            <Window filename="no-examples.http" breathing={false}>
              <div className="p-8 text-center font-mono text-sm text-slate-400" data-testid="developer-api-empty">
                <div className="mx-auto h-10 w-10 rounded-xl bg-white/5 flex items-center justify-center ring-1 ring-white/10 mb-3">
                  <Code2 className="h-5 w-5 text-emerald-300" />
                </div>
                Add API examples from the CMS to bring this section to life.
              </div>
            </Window>
          ) : (
            <Window filename="loading.http" breathing={false}>
              <div className="p-12 flex items-center justify-center" data-testid="developer-api-loading">
                <Loader2 className="h-5 w-5 animate-spin text-emerald-300/70" />
              </div>
            </Window>
          )}
        </motion.div>

        {/* RIGHT — ~38% copy */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="min-w-0 order-1 lg:order-2 lg:pl-2 xl:pl-4"
          data-testid="developer-api-copy"
        >
          <p className="text-xs sm:text-sm uppercase tracking-[0.24em] font-bold text-emerald-600 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {eyebrow}
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl xl:text-[2.75rem] font-extrabold text-slate-900 tracking-tight leading-[1.08]" style={{ fontFamily: "Outfit, sans-serif" }}>
            {heading}
          </h2>
          {subtitle && (
            <p className="mt-3 text-[15px] sm:text-base font-semibold text-slate-700 leading-snug" data-testid="developer-api-subtitle">
              {subtitle}
            </p>
          )}
          <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">
            {description}
          </p>

          <ul className="mt-7 space-y-2.5" data-testid="developer-api-bullets">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-center gap-3 text-sm sm:text-[15px] text-slate-700">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                <span className="font-medium">{b}</span>
              </li>
            ))}
          </ul>

          <a
            href={cta.href || "#"}
            target={cta.href?.startsWith("http") ? "_blank" : undefined}
            rel={cta.href?.startsWith("http") ? "noopener noreferrer" : undefined}
            data-testid="developer-api-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-5 h-11 group transition-colors"
          >
            {cta.label || "Explore API Documentation"}
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default DeveloperApi;
